import {
  AudioOutlined,
  CloseOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { useEffect, useRef } from 'react'

import { VoiceInput } from './useVoiceInput'

export const VOICE_INPUT_LABEL =
  'Voice input — speak your question; audio is transcribed in Australia and not stored'

/** 75 → "1:15". */
export const formatElapsed = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

/**
 * The composer's microphone. Idle: the microphone button. Recording: a pulsing red dot with the
 * time, Cancel, and Stop (which transcribes). Transcribing: a spinner, and Cancel.
 */
const VoiceInputControls = ({
  voice,
  disabled,
}: {
  voice: VoiceInput
  disabled: boolean
}) => {
  const stopRef = useRef<HTMLButtonElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)

  // The button that had the focus goes away as the state moves on: the focus goes to Stop while
  // recording, else to Cancel when it was left nowhere, so the keyboard (Enter, Escape) still
  // reaches the recording - and a field the user moved to keeps it.
  useEffect(() => {
    if (voice.state === 'recording') {
      stopRef.current?.focus()
    } else if (
      voice.state !== 'idle' &&
      (!document.activeElement || document.activeElement === document.body)
    ) {
      cancelRef.current?.focus()
    }
  }, [voice.state])

  if (voice.state === 'idle') {
    return (
      <button
        type="button"
        className="kw-composer-tool"
        title={VOICE_INPUT_LABEL}
        aria-label={VOICE_INPUT_LABEL}
        disabled={disabled}
        onClick={() => voice.start()}
      >
        <AudioOutlined />
      </button>
    )
  }

  const recording = voice.state === 'recording'
  return (
    <>
      {recording ? (
        <span
          className="kw-voice-timer"
          role="timer"
          aria-label="Recording time"
        >
          <span className="kw-voice-dot" aria-hidden />
          {formatElapsed(voice.elapsedSeconds)}
        </span>
      ) : (
        <span
          className="kw-voice-timer"
          role="status"
          aria-label={
            voice.state === 'starting'
              ? 'Waiting for the microphone'
              : 'Transcribing'
          }
        >
          <LoadingOutlined />
        </span>
      )}
      <button
        ref={cancelRef}
        type="button"
        className="kw-composer-tool"
        title={
          voice.state === 'transcribing'
            ? 'Cancel transcription'
            : 'Cancel recording'
        }
        aria-label={
          voice.state === 'transcribing'
            ? 'Cancel transcription'
            : 'Cancel recording'
        }
        onClick={voice.cancel}
      >
        <CloseOutlined />
      </button>
      {recording && (
        <button
          ref={stopRef}
          type="button"
          className="kw-composer-tool kw-voice-stop"
          title="Stop and transcribe"
          aria-label="Stop and transcribe"
          onClick={voice.stop}
        >
          <span className="kw-voice-stop-icon" aria-hidden />
        </button>
      )}
    </>
  )
}

export default VoiceInputControls
